/**
 * Which shapes of the vendored body model one contract muscle group paints, and on which view.
 *
 * **This table is MOBILE's, entry for entry** — `ClientMuscleMap`'s `GROUP_TO_SLUG` in
 * `move-with-me-frontend`, transcribed on 12 August 2026 together with the artwork it addresses.
 * The two apps draw one athlete, and a muscle lit on the phone and grey on the desktop is a
 * disagreement about the same data. Read that file before editing this one.
 *
 * ── It lives beside the ARTWORK since 3 September 2026, on its second consumer ────────────────
 * It was `pages/client-detail/model/muscleVolume.ts`'s private constant. The plan library's
 * inspector fills the same silhouette from a plan's muscle split (owner decision D4 in
 * `docs/design/kanon-programy-2026-09-03/README.md`), and a page may not import another page's
 * internals — so the mapping moved to the module that owns the shapes it names. That is also
 * where it belongs on its own terms: which slug a group paints is a fact about this artwork, and
 * it changes when the artwork does, not when a screen does.
 *
 * ── The KEY is the contract's spelling, and that is deliberate ───────────────────────────────
 * `Chest`, not `chest`. Both consumers read a muscle group off the wire and look it up here
 * without a normalisation step in between, so a group the backend adds is absent from this table
 * rather than silently mapped to something plausible. Both call sites use `Object.hasOwn` rather
 * than a bare index, and that is the site where it MATTERS rather than merely tidies: a wire value
 * of `constructor` resolves through `Object.prototype` to a function, which is not nullish, so
 * `?? []` would never fire and a caller would iterate a function.
 *
 * Two entries changed when the artwork did, and both are mobile's answer rather than a new opinion:
 *
 * · **`Shoulders` paints the FRONT deltoid only.** The old web library split the muscle into
 *   `front-deltoids` / `back-deltoids`; this artwork has one `deltoids` shape per view, and mobile
 *   lights the front one. The back view's own deltoid stays neutral, exactly as it does on a phone.
 * · **`Back` is upper plus lower, and no longer the trapezius.** `trapezius` is its own shape here
 *   and the contract has no group that owns it, so lighting it from `Back` would attribute pull
 *   volume to a muscle the backend never named.
 *
 * `Other` is the contract's bucket for sets it could not attribute. It has no anatomy and is
 * deliberately absent rather than painted somewhere plausible. `FullBody` is absent for the same
 * reason: it names every shape or none, and lighting the whole figure from one exercise would
 * drown every real attribution beside it.
 */

import type { BodySlug } from './bodyArtwork';

/** One shape of the silhouette, and which of the two views carries it. */
export interface BodyRegion {
  readonly muscle: BodySlug;
  readonly side: 'front' | 'back';
}

export const REGIONS: Readonly<Record<string, readonly BodyRegion[]>> = {
  Chest: [{ muscle: 'chest', side: 'front' }],
  Back: [
    { muscle: 'upper-back', side: 'back' },
    { muscle: 'lower-back', side: 'back' },
  ],
  Shoulders: [{ muscle: 'deltoids', side: 'front' }],
  Biceps: [{ muscle: 'biceps', side: 'front' }],
  Triceps: [{ muscle: 'triceps', side: 'back' }],
  Forearms: [{ muscle: 'forearm', side: 'front' }],
  Abs: [{ muscle: 'abs', side: 'front' }],
  Obliques: [{ muscle: 'obliques', side: 'front' }],
  Quads: [{ muscle: 'quadriceps', side: 'front' }],
  Hamstrings: [{ muscle: 'hamstring', side: 'back' }],
  Glutes: [{ muscle: 'gluteal', side: 'back' }],
  Calves: [{ muscle: 'calves', side: 'back' }],
};
